/*
 *   Copyright (c) 2025 LoginID Inc
 *   All rights reserved.

 *   Licensed under the Apache License, Version 2.0 (the "License");
 *   you may not use this file except in compliance with the License.
 *   You may obtain a copy of the License at

 *   http://www.apache.org/licenses/LICENSE-2.0

 *   Unless required by applicable law or agreed to in writing, software
 *   distributed under the License is distributed on an "AS IS" BASIS,
 *   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *   See the License for the specific language governing permissions and
 *   limitations under the License.
 */

import { Button, Grid, Text, Divider, Image, Container } from "@mantine/core";
import {
  IconCreditCard,
  IconFaceId,
  IconFingerprint,
} from "@tabler/icons-react";
import { LIDService } from "@/services/loginid";
import ParseUtil from "@/lib/parse";
import { CheckoutRequest } from ".";
import { useState } from "react";

export interface TxPayload {
  Merchant: string;
  Subtotal: string;
  Shipping: string;
  Tax: string;
  Total: string;
  At: Date;
  Address: Address;
  Contact: string;
  Email: string;
  Note: string;
}

export interface Address {
  Street: string;
  City: string;
  State: string;
  Country: string;
  Postal: string;
}

export interface CheckoutAgentResultProps {
  username: string;
  token: string;
  request: CheckoutRequest;
}

/**
 * CheckoutAgentResult
 *
 * This component displays the payment summary to the user and handles passkey-based transaction confirmation.
 *
 * Responsibilities:
 * - Displays a breakdown of the order details (shipping, contact info, total).
 * - Triggers passkey-based transaction authentication with the LoginID Wallet SDK (`performAction("passkey:tx")`).
 * - Handles both success (payload signature returned) and failure (error shown).
 *
 * Flow Summary:
 * 1. Show the user a detailed confirmation page with merchant and payment info.
 * 2. When the user clicks "Confirm", initiate passkey authentication.
 * 3. On successful signing, complete the checkout flow by passing token back to the parent.
 */
export function CheckoutAgentResult(props: CheckoutAgentResultProps) {
  const payData: TxPayload = {
    Merchant: props.request.merchant,
    Subtotal: props.request.subtotal,
    Tax: props.request.tax,
    Shipping: props.request.shipping,
    Total: props.request.total,
    At: new Date(),
    Contact: "John Smith",
    Email: props.username,
    Address: {
      Street: "1 Front St.",
      City: "Toronto",
      State: "ON",
      Country: "Canada",
      Postal: "N3J4P8",
    },
    Note: "This payment will not be immediately reflected in your current balance.",
  };


  return (
    <Container w="100%">
      <Grid>
        <Grid.Col span={12} ta="center">
          <Text c="green.5" size="xl">Transaction Successful!</Text>
        </Grid.Col>
      </Grid>
      <Divider mb="xs" mt="xs"></Divider>
      <Grid mt={0}>
        <Grid.Col span={6}>
          <Text fw={500} ta="right" size="xs">
            SUBTOTAL
          </Text>
        </Grid.Col>
        <Grid.Col span={6}>
          <Text size="xs" ta="left">
            {payData.Subtotal}{" "}
          </Text>
        </Grid.Col>
      </Grid>
      <Grid mt={0}>
        <Grid.Col span={6}>
          <Text fw={500} ta="right" size="xs">
            SHIPPING
          </Text>
        </Grid.Col>
        <Grid.Col span={6}>
          <Text size="xs" ta="left">
            {payData.Shipping}{" "}
          </Text>
        </Grid.Col>
      </Grid>
      <Grid mt={0}>
        <Grid.Col span={6}>
          <Text fw={500} ta="right" size="xs">
            TAX
          </Text>
        </Grid.Col>
        <Grid.Col span={6}>
          <Text size="xs" ta="left">
            {payData.Tax}{" "}
          </Text>
        </Grid.Col>
      </Grid>
      <Grid>
        <Grid.Col span={6}>
          <Text fw={700} ta="right">
            PAY {payData.Merchant}{" "}
          </Text>
        </Grid.Col>
        <Grid.Col span={6} ta="left">
          ${payData.Total}
        </Grid.Col>
      </Grid>

      <Divider mb="xs" mt="xs"></Divider>
      <Grid>
        <Grid.Col span={12} ta="center">
          <Text >Please return to your agent to finalize the payment with the following token {ParseUtil.sanitizedDisplayToken(props.token)}...</Text>
        </Grid.Col>
      </Grid>

    </Container>
  );
}
